import { useContext, useEffect, useMemo, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Container, Icon, IconButton, Tooltip, Typography } from '@mui/material';

import Form from 'Lib/components/form/Form';
import withForm from 'Lib/components/hoc/withForm';
import SideDrawer, { DRAWER_CONTENT_TYPES } from 'Lib/components/layout/components/SideDrawer';
import { allowOwnerShare, allowTenantShare, allowVendorShare } from 'Lib/constants/app/noteShareTypes';
import ContactTypes from 'Lib/constants/contacts/contactTypes';
import SidebarContext from 'Lib/contexts/SidebarContext';
import useApi from 'Lib/hooks/useApi';
import useNotify from 'Lib/hooks/useNotify';
import UploadField from 'Lib/muiComponents/form/UploadField';
import WizzyNotes from 'Lib/muiComponents/Wizzy/instances/Notes';
import getPagination from 'Lib/utilities/getPagination';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';

import ChecklistField from '../../form/ChecklistField';
import DatePickerField from '../../form/DatePickerField';

import NotesContext from './context';
import { Header } from './Header';
import { Note } from './Note';
import { EmptyContainer, EmptyText, GroupedHeader, NewNoteButton, NoteIcon, NoteIconBackground } from './styledComponents';

export const SORT_TYPES = { NEWEST: 'Newest', OLDEST: 'Oldest' };

const WrappedNotesButton = withForm(NotesButton);

function NotesButton({ toggleDrawer, ...rest }) {
	const { mobile } = screenSize();

	return (
		<>
			<Tooltip title="Notes" arrow placement="top">
				<Box sx={{ mx: { xs: 0, sm: 2 } }}>
					{mobile ? (
						<IconButton onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.NOTES)}>note_alt</IconButton>
					) : (
						<IconButton variant="regular" onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.NOTES)}>
							note_alt
						</IconButton>
					)}
				</Box>
			</Tooltip>
			<NotesContainer {...rest} />
		</>
	);
}

const LoadingIndicator = () => (
	<Container sx={{ textAlign: 'center', mt: 10 }}>
		<CircularProgress color="success" />
	</Container>
);

const EmptyMessage = ({ toggleAdd }) => (
	<EmptyContainer>
		<NoteIconBackground>
			<NoteIcon>note_alt</NoteIcon>
		</NoteIconBackground>
		<EmptyText>It seems you haven&apos;t created any notes yet.</EmptyText>
		<NewNoteButton variant="primary" onClick={toggleAdd} sx={{ mt: 4 }}>
			Create a New Note
		</NewNoteButton>
	</EmptyContainer>
);

const FormikFields = ({ formik, apis, previews, upload }) => {
	const {
		components: { TagField },
		sidebar
	} = useContext(SidebarContext);
	const { objectTypeID } = sidebar;

	const shareOptions = useMemo(() => {
		const shareOptions = [];

		if (allowOwnerShare(objectTypeID)) {
			shareOptions.push({ label: 'Owner', name: 'isSharedWithOwner', value: 1 });
		}

		if (allowTenantShare(objectTypeID)) {
			shareOptions.push({ label: 'Tenant', name: 'isSharedWithTenant', value: 1 });
		}

		if (allowVendorShare(objectTypeID)) {
			shareOptions.push({ label: 'Vendor', name: 'isSharedWithVendor', value: 1 });
		}

		return shareOptions;
	}, [objectTypeID]);

	return (
		<>
			<WizzyNotes apis={apis} />
			<TagField
				multiple
				fullObject
				id="tags"
				name="tags"
				label="Tag"
				placeholder="Add a tag"
				aria-describedby="tag-helper-text"
				helperText="Tag names need to be one continuous word, but you can use hyphens and underscores to make tag names longer. You can add multiple tags to a note."
			/>
			<Typography
				sx={{
					fontSize: theme => theme.typography.pxToRem(14),
					fontWeight: 'medium',
					letterSpacing: '0.1px',
					mt: 3,
					mb: 2
				}}>
				Attach files to Note
			</Typography>
			<UploadField name="fileAttachments" filePath="file.fileID" defaultPreviews={previews} upload={upload} />

			{shareOptions.length > 0 && <ChecklistField label="Share Notes" options={shareOptions} />}

			{formik.values.isSharedWithOwner == 1 && (
				<DatePickerField id="datePosted" name="datePosted" label="Statement Posting Date" helperText="Requires notes module to be enabled" />
			)}
		</>
	);
};

const Notes = ({ isDeleting, handlePrint, handleDelete, toggleEdit, togglePin, toggleShare }) => {
	const { sort, pinnedNotes, notes } = useContext(NotesContext);

	const groupNotesByYear = () => {
		const groupedNotes = {};

		notes.forEach(note => {
			const year = note.note.dateTimeCreated.split('-')[0];

			if (!groupedNotes[year]) {
				groupedNotes[year] = [];
			}

			groupedNotes[year].push(note);
		});

		return groupedNotes;
	};

	const groupedNotes = groupNotesByYear();

	return (
		<Container disableGutters>
			{!isEmpty(pinnedNotes) && (
				<GroupedHeader variant="caption" sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
					<Icon sx={{ mr: 1 }}>push_pin</Icon> Pinned
				</GroupedHeader>
			)}
			{pinnedNotes.map(pinnedNote => (
				<Note
					pinned
					key={pinnedNote.note.noteID}
					note={pinnedNote}
					isDeleting={isDeleting}
					handlePrint={() => handlePrint(pinnedNote)}
					handleDelete={() => handleDelete(pinnedNote)}
					toggleEdit={() => toggleEdit(pinnedNote)}
					togglePin={() => togglePin(pinnedNote)}
					toggleShare={type => toggleShare(pinnedNote, type)}
				/>
			))}
			{Object.keys(groupedNotes)
				.sort((a, b) => {
					if (sort == SORT_TYPES.NEWEST) {
						return b - a;
					} else {
						return a - b;
					}
				})
				.map(year => (
					<div key={year}>
						<GroupedHeader variant="caption">{year}</GroupedHeader>
						{groupedNotes[year].map(note => (
							<Note
								key={note.note.noteID}
								note={note}
								isDeleting={isDeleting}
								handlePrint={() => handlePrint(note)}
								handleDelete={() => handleDelete(note)}
								toggleEdit={() => toggleEdit(note)}
								togglePin={() => togglePin(note)}
								toggleShare={type => toggleShare(note, type)}
							/>
						))}
					</div>
				))}
		</Container>
	);
};

const NotesContent = ({
	isLoading,
	isDeleting,
	formik,
	upload,
	previews,
	apis,
	showEmptyMessage,
	showForm,
	handleDelete,
	handlePrint,
	toggleEdit,
	togglePin,
	toggleShare,
	toggleAdd
}) => {
	return isLoading ? (
		<LoadingIndicator />
	) : showForm ? (
		<FormikFields formik={formik} apis={apis} previews={previews} upload={upload} />
	) : showEmptyMessage ? (
		<EmptyMessage toggleAdd={toggleAdd} />
	) : (
		<Notes isDeleting={isDeleting} handleDelete={handleDelete} handlePrint={handlePrint} toggleEdit={toggleEdit} togglePin={togglePin} toggleShare={toggleShare} />
	);
};

function NotesContainer({ form }) {
	const { toastError, toastSuccess } = useNotify();
	const { isDrawerOpen, drawerContentTypeID } = useSelector(state => state.ui);
	const { sidebar, apis } = useContext(SidebarContext);
	const { objectID, objectTypeID } = sidebar;

	const [isLoading, setIsLoading] = useState(true);
	const [isSearching, setIsSearching] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const [search, setSearch] = useState('');
	const [previews, setPreviews] = useState([]);
	const [sort, setSort] = useState(SORT_TYPES.NEWEST);
	const [pagination, setPagination] = useState({});

	const [note, setNote] = useState({});
	const [notes, setNotes] = useState([]);
	const [pinnedNotes, setPinnedNotes] = useState([]);

	const pinNote = useApi(apis.pinNote);
	const getNotes = useApi(apis.getNotes);
	const updateNote = useApi(apis.updateNote);
	const deleteNote = useApi(apis.deleteNote);
	const uploadFile = useApi(apis.uploadFile);
	const shareNoteWithOwner = useApi(apis.shareNoteWithOwner);
	const shareNoteWithTenant = useApi(apis.shareNoteWithTenant);
	const shareNoteWithVendor = useApi(apis.shareNoteWithVendor);

	const showForm = isAdding || isEditing;
	const showEmptyMessage = isEmpty(search) && isEmpty(notes) && isEmpty(pinnedNotes);
	const orderBy = sort == SORT_TYPES.NEWEST ? 'note.dateTimeCreated|desc' : 'note.dateTimeCreated|asc';

	useEffect(() => {
		setIsAdding(false);
		setIsEditing(false);

		if (isDrawerOpen && drawerContentTypeID === DRAWER_CONTENT_TYPES.NOTES) {
			fetchNotes();
		} else {
			// Reset state on closing of drawer
			setNote({});
			setNotes([]);
			setPreviews([]);
			setSort(SORT_TYPES.NEWEST);
		}
	}, [isDrawerOpen]);

	useEffect(() => {
		if (!isLoading) {
			setIsLoading(true);
			setNotes([]);
			fetchNotes();
		}
	}, [sort]);

	const fetchPinnedNotes = params => {
		return getNotes(objectTypeID, objectID, { isPinned: '1', orderBy, search, ...params }).then(response => {
			setPinnedNotes(response.data);
		});
	};

	const fetchUnpinnedNotes = params => {
		return getNotes(objectTypeID, objectID, { isPinned: '0', orderBy, search, ...params }).then(response => {
			setNotes(response.data);
			setPagination(getPagination(response));
		});
	};

	const fetchNextPageOfNotes = params => {
		return getNotes(objectTypeID, objectID, { isPinned: '0', orderBy, search, ...params }).then(response => {
			setNotes(prevState => [...prevState, ...response.data.filter(note => !prevState.find(prevNote => prevNote.note?.noteID == note.note?.noteID))]);
			setPagination(getPagination(response));
		});
	};

	const fetchNotes = () => {
		setIsLoading(true);

		Promise.all([fetchPinnedNotes(), fetchUnpinnedNotes()]).then(() => {
			setIsLoading(false);
		});
	};

	const getNextPage = () => {
		// Avoid fetching next page if we've already reached the last
		if (pagination.page == pagination.lastPage) return;
		fetchNextPageOfNotes({ page: parseInt(pagination.page) + 1 });
	};

	const searchNotes = search => {
		setSearch(search);
		setIsSearching(true);
		setIsLoading(true);

		getNotes(objectTypeID, objectID, { search }).then(response => {
			setPinnedNotes(response.data.filter(note => note.note.isPinned == '1'));
			setNotes(response.data.filter(note => note.note.isPinned == '0'));
			setIsSearching(false);
			setIsLoading(false);
		});
	};

	const handleSort = key => {
		setSort(SORT_TYPES[key]);
	};

	const handleSubmit = (values, formik) => {
		const noteID = note.noteID;

		// Normalize the tags
		const tags = values.tags.map(tag => (typeof tag === 'string' ? tag : tag.label));

		const data = {
			...values,
			tags,
			objectID,
			objectTypeID
		};

		form.handlePromise(formik, [updateNote(noteID, data)])
			.then(response => {
				const updatedNote = response.data;
				setNotes(prevNotes => prevNotes.map(n => (n.note.noteID === noteID ? updatedNote : n)));
				setPinnedNotes(prevPinnedNotes => prevPinnedNotes.map(n => (n.note.noteID === noteID ? updatedNote : n)));

				setIsAdding(false);
				setIsEditing(false);
				setNote({});
				setPreviews([]);
				fetchNotes();
				toastSuccess(noteID ? 'Note updated successfully' : 'Note created successfully');
				formik.resetForm();
			})
			.catch(error => {
				toastError(error);
			});
	};

	const handleFileDelete = (file, formik) => {
		const newPreviews = previews.filter(preview => preview.fileID !== file.fileID);

		// Set updated previews for state and formik
		setPreviews(newPreviews);
		formik.setFieldValue(
			'fileAttachments',
			newPreviews.map(preview => preview.fileID)
		);
	};

	const handleDelete = note => {
		setIsDeleting(true);

		deleteNote(note.note.noteID)
			.then(() => {
				if (note.note.isPinned == 1) {
					setPinnedNotes(prevState => prevState.filter(n => n.note.noteID !== note.note.noteID));
				} else {
					setNotes(prevState => prevState.filter(n => n.note.noteID !== note.note.noteID));
				}
				toastSuccess('Note deleted successfully');
			})
			.catch(error => {
				toastError(error);
			})
			.finally(() => setIsDeleting(false));
	};

	const handlePrint = note => {
		return window.open(`/api/manager/notes/${note.note.noteID}/print`, '_blank');
	};

	const handlePrintAll = () => {
		return window.open(`/api/manager/notes/${objectTypeID}/${objectID}/print`, '_blank');
	};

	const toggleAdd = () => setIsAdding(!isAdding);

	const toggleEdit = ({ fileAttachments, note, tags }) => {
		setNote({
			tags,
			...note
		});
		setPreviews(fileAttachments.map(({ file }) => ({ label: file.title, fileID: file.fileID })));
		setIsEditing(!isEditing);
	};

	const togglePin = note => {
		const isPinned = note.note.isPinned == '1';

		pinNote(note.note.noteID, { isPinned: isPinned ? 0 : 1 })
			.then(() => {
				setNotes([]);
				setPinnedNotes([]);
				fetchNotes();
			})
			.catch(error => {
				toastError(error);
			});
	};

	const toggleShare = (note, contactTypeID) => {
		const type = `${ContactTypes.getName(contactTypeID)}`;
		const isSharing = note.note[`isSharedWith${type}`] == 1;
		let endpoint = null;

		if (contactTypeID === ContactTypes.Owner) {
			endpoint = shareNoteWithOwner;
		} else if (contactTypeID === ContactTypes.Vendor) {
			endpoint = shareNoteWithVendor;
		} else if (contactTypeID === ContactTypes.Tenant) {
			endpoint = shareNoteWithTenant;
		}

		const data = {
			isShared: !isSharing
		};

		endpoint(note.note.noteID, data)
			.then(response => {
				setNotes(prevState => prevState.map(n => (n.note.noteID === note.note.noteID ? response.data : n)));
				setPinnedNotes(prevState => prevState.map(n => (n.note.noteID === note.note.noteID ? response.data : n)));
			})
			.catch(error => {
				toastError(error);
			});
	};

	const handleCancel = formik => {
		setPreviews([]);
		setNote({});
		setIsAdding(false);
		setIsEditing(false);
		formik.resetForm();
	};

	const contentProps = {
		isLoading,
		isDeleting,
		showEmptyMessage,
		previews,
		showForm,
		apis,
		upload: uploadFile,
		handleFileDelete,
		handleDelete,
		handlePrint,
		toggleEdit,
		togglePin,
		toggleShare,
		toggleAdd
	};

	const scrollRef = useBottomScrollListener(getNextPage, { debounce: 300 });
	const fileAttachments = previews.map(preview => preview.fileID);

	return (
		<Form
			enableReinitialize
			key={isDrawerOpen}
			initialValues={{ fileAttachments, tags: [], ...note }}
			onSubmit={handleSubmit}
			render={formik => (
				<form onSubmit={formik.handleSubmit}>
					<NotesContext.Provider value={{ notes, pinnedNotes, search, sort, showForm }}>
						<SideDrawer
							size="lg"
							drawerContentTypeID={DRAWER_CONTENT_TYPES.NOTES}
							title={isAdding ? 'New Note' : isEditing ? 'Edit Note' : 'Notes'}
							ButtonProps={{ submitText: 'Save' }}
							contentRef={scrollRef}
							contentHeader={
								!showEmptyMessage ? (
									<Header isLoading={isSearching} handlePrintAll={handlePrintAll} toggleAdd={toggleAdd} onSort={handleSort} onSearch={searchNotes} />
								) : null
							}
							hideFooter={!showForm}
							loading={formik.isSubmitting}
							onSubmit={formik.handleSubmit}
							onCancel={() => handleCancel(formik)}>
							<NotesContent formik={formik} {...contentProps} />
						</SideDrawer>
					</NotesContext.Provider>
				</form>
			)}
		/>
	);
}

export { WrappedNotesButton as NotesButton };
